import { t } from "@lingui/macro";
import {
  CircleNotchIcon,
  DownloadSimpleIcon,
  PaintBrushIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { Button, ScrollArea, Separator, Tooltip } from "@reactive-resume/ui";
import { useRef, useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { usePrintResume } from "@/client/services/resume";
import { useResumeStore } from "@/client/stores/resume";

import { AiDetectorSection } from "./sections/ai-detector";
import { CssSection } from "./sections/css";
import { LayoutSection } from "./sections/layout";
import { PageSection } from "./sections/page";
import { TemplateSection } from "./sections/template";
import { ThemeSection } from "./sections/theme";
import { TypographySection } from "./sections/typography";

const openInNewTab = (url: string) => {
  const win = window.open(url, "_blank");
  if (win) win.focus();
};

export const RightSidebar = () => {
  const { toast } = useToast();
  const containterRef = useRef<HTMLDivElement | null>(null);
  const [activeMode, setActiveMode] = useState<"designing" | "ai-detector">("designing");

  const id = useResumeStore((state) => state.resume.id);
  const { printResume, loading: printLoading } = usePrintResume();

  const onDownloadPdf = async () => {
    const { url } = await printResume({ id });

    if (url) {
      openInNewTab(url);
    } else {
      toast({
        variant: "error",
        title: t`Oops, something went wrong.`,
        description: t`The server was unable to generate your resume. Please try again later.`,
      });
    }
  };

  const scrollIntoView = (selector: string) => {
    const section = containterRef.current?.querySelector(selector);
    section?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex bg-secondary-accent/30">
      <ScrollArea orientation="vertical" className="h-screen flex-1 pb-16 lg:pb-0">
        <div className="flex items-center justify-center border-b border-border p-4 sm:hidden">
          <h2 className="text-lg font-semibold">
            {activeMode === "designing" ? t`Customize` : t`AI & ATS Checker`}
          </h2>
        </div>
        
        {/* Mode Content */}
        {activeMode === "designing" ? (
          <div ref={containterRef} className="grid gap-y-6 p-6 @container/right">
            <TemplateSection />
            <Separator />
            <LayoutSection />
            <Separator />
            <TypographySection />
            <Separator />
            <ThemeSection />
            <Separator />
            <CssSection />
            <Separator />
            <PageSection />
          </div>
        ) : (
          <div className="p-6">
            <AiDetectorSection />
          </div>
        )}
      </ScrollArea>

      <div className="hidden basis-12 flex-col items-center bg-secondary-accent/30 py-4 sm:flex">
        <div className="flex flex-col items-center gap-y-3">
          <Tooltip content={t`Designing`}>
            <Button
              size="icon"
              variant={activeMode === "designing" ? "primary" : "ghost"}
              onClick={() => setActiveMode("designing")}
              className="size-8"
            >
              <PaintBrushIcon size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t`AI & ATS Checker`}>
            <Button
              size="icon"
              variant={activeMode === "ai-detector" ? "primary" : "ghost"}
              onClick={() => setActiveMode("ai-detector")}
              className="size-8"
            >
              <SparkleIcon size={18} />
            </Button>
          </Tooltip>
          <Tooltip content={t`Download PDF`}>
            <Button
              size="icon"
              variant="ghost"
              disabled={printLoading}
              onClick={onDownloadPdf}
              className="size-8"
            >
              {printLoading ? (
                <CircleNotchIcon size={18} className="animate-spin" />
              ) : (
                <DownloadSimpleIcon size={18} />
              )}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
